import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { locationsApi } from '../../api/locations';
import type { LocationInput, ProductLocationPricingInput, BulkPricingInput, SuburbInput } from '../../types/ecommerce';

const KEY = ['locations'] as const;

export function useLocations(includeInactive = false, regionId?: string) {
  return useQuery({ queryKey: [...KEY, includeInactive, regionId], queryFn: () => locationsApi.list(includeInactive, regionId) });
}

// ── Hierarchy: Country → Region → City(Location, above) → Suburb ──

export function useLocationCountries() {
  return useQuery({ queryKey: ['location-countries'], queryFn: () => locationsApi.listCountries(), staleTime: 5 * 60_000 });
}

export function useLocationRegions(countryId?: string) {
  return useQuery({
    queryKey: ['location-regions', countryId],
    queryFn: () => locationsApi.listRegions(countryId),
    staleTime: 5 * 60_000,
  });
}

export function useSuburbs(locationId: string | null) {
  return useQuery({
    queryKey: ['suburbs', locationId],
    queryFn: () => locationsApi.listSuburbs(locationId as string),
    enabled: !!locationId,
  });
}

export function useCreateSuburb(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SuburbInput) => locationsApi.createSuburb(locationId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suburbs', locationId] }),
  });
}

export function useDeleteSuburb(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (suburbId: string) => locationsApi.removeSuburb(locationId, suburbId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suburbs', locationId] }),
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocationInput) => locationsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LocationInput }) => locationsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => locationsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCopyLocationPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string }) => locationsApi.copyTo(id, targetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useProductLocationPricing(productId: string | null) {
  return useQuery({
    queryKey: ['product-location-pricing', productId],
    queryFn: () => locationsApi.getProductPricing(productId as string),
    enabled: !!productId,
  });
}

export function useSetProductLocationPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: ProductLocationPricingInput }) =>
      locationsApi.setProductPricing(productId, body),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['product-location-pricing', vars.productId] }),
  });
}

export function useBulkPricing() {
  return useMutation({
    mutationFn: (body: BulkPricingInput) => locationsApi.bulkPricing(body),
  });
}
